import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { TicketCard } from "@/components/TicketCard";
import { CheckCircle, Spinner, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const orderIdParam = params.get("order_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const isIpaymu = provider === "ipaymu";
    const isMidtrans = provider === "midtrans";
    const usesOrderId = isIpaymu || isMidtrans;
    if (usesOrderId ? !orderIdParam : !sessionId) { setStatus("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts.current += 1;
      try {
        if (usesOrderId) {
          // public endpoint: fulfills if paid & returns tickets (works without login)
          const { data } = await api.get(`/orders/${orderIdParam}/tickets`);
          if (data.paid) {
            if (active) { setOrder({ tickets: data.tickets }); setStatus("paid"); }
            return;
          }
        } else {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            const ord = await api.get(`/orders/${data.order_id}/tickets`);
            if (active) { setOrder({ tickets: ord.data.tickets }); setStatus("paid"); }
            return;
          }
        }
      } catch (e) { /* keep polling */ }
      if (attempts.current >= 20) { if (active) setStatus("timeout"); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, provider, orderIdParam]);

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 py-16">
        {status === "checking" && (
          <div className="text-center py-20">
            <Spinner size={48} className="text-primary animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-black">Memverifikasi pembayaran...</h1>
            <p className="text-zinc-500 mt-2">Mohon tunggu sebentar.</p>
          </div>
        )}
        {status === "paid" && order && (
          <div>
            <div className="text-center mb-10">
              <CheckCircle weight="fill" size={64} className="text-[#00FF66] mx-auto mb-4" />
              <h1 data-testid="payment-success-title" className="font-heading text-4xl font-black tracking-tight">Pembayaran Berhasil!</h1>
              <p className="text-zinc-400 mt-2">E-Ticket Anda sudah terbit dan dikirim ke email.</p>
            </div>
            <div className="space-y-5">
              {order.tickets?.map((t) => <TicketCard key={t.id} ticket={t} />)}
            </div>
            <div className="text-center mt-8">
              <Link to="/tickets"><Button className="bg-primary text-black font-bold hover:bg-cyan-300 rounded-none">Lihat Semua Tiket Saya</Button></Link>
            </div>
          </div>
        )}
        {(status === "error" || status === "timeout") && (
          <div className="text-center py-20">
            <XCircle weight="fill" size={64} className="text-secondary mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-black">Verifikasi tertunda</h1>
            <p className="text-zinc-500 mt-2">Pembayaran mungkin masih diproses. Cek "Tiket Saya" beberapa saat lagi.</p>
            <Link to="/tickets"><Button className="mt-6 bg-primary text-black font-bold rounded-none">Tiket Saya</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}
